import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, DatePicker, Modal, Spin } from 'antd';
import {
    FullscreenOutlined,
    FullscreenExitOutlined,
    DownOutlined,
} from '@ant-design/icons';
import { useApolloClient, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import * as mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
    QUERY_DELIVERY_MONITOR_HUBS,
    QUERY_DELIVERY_MONITOR_WAREHOUSES,
    QUERY_DELIVERY_MONITOR_DAILY_KPI_WITH_PREVIOUS,
    QUERY_DELIVERY_MONITOR_DRIVERS,
    QUERY_DELIVERY_MONITOR_DRIVER_POSITIONS,
    QUERY_DELIVERY_MONITOR_DRIVER_DETAIL,
    QUERY_DELIVERY_MONITOR_DRIVER_TRAJECTORY,
} from '../../../graphql/queries/deliveryMonitor/queryDeliveryMonitor';
import {
    LastMileSiteType,
    QueryDeliveryMonitorHubsResponse,
    QueryDeliveryMonitorWarehousesResponse,
    QueryDeliveryMonitorDailyKpiWithPreviousResponse,
    DeliveryMonitorKpiSnapshotType,
    QueryDeliveryMonitorDriversResponse,
    QueryDeliveryMonitorDriverPositionsResponse,
    QueryDeliveryMonitorDriverDetailResponse,
    QueryDeliveryMonitorDriverTrajectoryResponse,
    DeliveryMonitorDriverType,
    DeliveryMonitorDriverPositionType,
    DeliveryMonitorProgressBucket,
    DeliveryMonitorDriverSort,
    DeliveryMonitorParcelStatus,
} from '../../../graphql/types/deliveryMonitor/DeliveryMonitorTypes';
import { MAPBOX_ACCESS_TOKEN } from '../../map-tool/aoi-platform/constants';
import { DASHBOARD_PACIFIC_TZ } from '../../../utils/dashboardPacificTime';
import { maskPersonNameForExport } from '../../../utils/exportDesensitize';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { SiteType } from '../../../graphql/types/SiteType';
import { useKpiInterpolation } from './useKpiInterpolation';
import { CarIcon, CrownIcon, WhsMarkerIcon } from './icons';
import {
    containsWithInset,
    DRIVER_LIST_BATCH,
    fitAllBounds,
    hubLabel,
    nextVisibleCount,
    pacificDayStartIso,
    pacificTaskDate,
    pickKingId,
    pickSavedSiteId,
    placePopup,
    planCarPresence,
    toMotionPoint,
    treeSitePick,
    whsFocusOnChange,
} from './deliveryMonitorMotion';
import { useCarGlide } from './useCarGlide';
import { useRouteReplay } from './useRouteReplay';
import swiftxLogo from './images/swiftx-logo.png';
import styles from './DeliveryMonitorPage.module.css';

dayjs.extend(utc);
dayjs.extend(timezone);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(mapboxgl as any).accessToken = MAPBOX_ACCESS_TOKEN;

const TEXT_DIM = '#7ea3c7';

// bucket 上色：LEADING 绿 / NORMAL 青 / LAGGING 黄。滞后是警告级不是错误级，故用 #ffcf3a 而非红。
const BUCKET_COLORS: Record<DeliveryMonitorProgressBucket, string> = {
    LEADING: '#4ee7a3',
    NORMAL: '#22d3ee',
    LAGGING: '#ffcf3a',
};

const POLL_POSITIONS_MS = 20_000;
// 与小车同频：两者同源于后端 getBucketedDrivers（15s 缓存），不同频会让列表妥投率落后地图最多 10 分钟
const POLL_DRIVERS_MS = POLL_POSITIONS_MS;
const POLL_KPI_MS = 600_000;
const MINUTE_MS = 60_000;

const DEFAULT_CENTER: [number, number] = [-95.7129, 37.0902];
const DEFAULT_ZOOM = 3.5;
// 换仓后以仓为中心的缩放级别：约覆盖仓周边 37.5 km 宽
const WHS_FOCUS_ZOOM = 12;

const siteLabel = (site: LastMileSiteType): string =>
    site.sortingCode?.trim() || site.name;

const formatCount = (n: number | null | undefined): string =>
    n === null || n === undefined ? '-' : Math.round(Number(n)).toLocaleString();

const formatPercentNumber = (n: number | null | undefined): string => {
    if (n === null || n === undefined || Number.isNaN(n)) return '-';
    return n.toFixed(2);
};

const formatPercentFromString = (v: string | null | undefined): string => {
    if (v === null || v === undefined || v === '') return '-';
    return `${v}%`;
};

// 后端计数字段是 Long，GraphQL 序列化后运行时是字符串（TS 类型标 number 只是编译期约定）。
// 之前用 `yesterday === 0` 严格等判 0，"0" !== 0 会漏过守卫，再靠 JS 算术把 "0" 转 0，
// 就出现 17/0 = Infinity → 卡片显示 "▲ Infinity%"。这里显式转数再判。
const computeCountDelta = (
    current: number | string | null | undefined,
    yesterday: number | string | null | undefined,
): number | null => {
    if (current === null || current === undefined) return null;
    if (yesterday === null || yesterday === undefined) return null;
    const c = Number(current);
    const y = Number(yesterday);
    if (Number.isNaN(c) || Number.isNaN(y) || y === 0) return null;
    return ((c - y) / y) * 100;
};

const formatCountDeltaPercent = (delta: number | null): string | null => {
    if (delta === null || !Number.isFinite(delta)) return null;
    const sign = delta > 0 ? '+' : '';
    return `${sign}${delta.toFixed(2)}%`;
};

const computeRateDeltaPp = (
    current: string | number | null | undefined,
    yesterday: string | number | null | undefined,
): number | null => {
    if (current === null || current === undefined) return null;
    if (yesterday === null || yesterday === undefined) return null;
    const c = Number(current);
    const y = Number(yesterday);
    if (Number.isNaN(c) || Number.isNaN(y)) return null;
    return c - y;
};

const formatRateDeltaPp = (pp: number | null): string | null => {
    if (pp === null || !Number.isFinite(pp)) return null;
    const sign = pp > 0 ? '+' : '';
    return `${sign}${pp.toFixed(2)}pp`;
};

// —— 头部时钟 —— //

const useNow = (): Date => {
    const [now, setNow] = useState<Date>(() => new Date());
    useEffect(() => {
        const id = window.setInterval(() => setNow(new Date()), 1000);
        return () => window.clearInterval(id);
    }, []);
    return now;
};

// 同一筛选范围内换变量（指定查询时刻每分钟步进）时沿用上一份数据：Apollo 换变量加载期间 data 为 undefined，
// 不兜底会每分钟闪一次转圈。范围变了不沿用，免得旧范围的 KPI 被补间写进新范围的基线
const useScopedPrevious = <T,>(data: T | undefined, scopeKey: string): T | undefined => {
    const lastRef = useRef<{ scopeKey: string; data: T } | null>(null);
    if (data !== undefined) lastRef.current = { scopeKey, data };
    if (data !== undefined) return data;
    return lastRef.current?.scopeKey === scopeKey ? lastRef.current.data : undefined;
};

// —— 指定查询时刻（三连击头部时钟打开，按美西时间输入）—— //

// DatePicker 以浏览器本地时区承载「美西墙钟」，提交时再按美西时区解释回 epoch ms
const toPacificWallClock = (ms: number): Dayjs =>
    dayjs(dayjs(ms).tz(DASHBOARD_PACIFIC_TZ).format('YYYY-MM-DD HH:mm:ss'));
const fromPacificWallClock = (value: Dayjs): number =>
    dayjs.tz(value.format('YYYY-MM-DD HH:mm:ss'), DASHBOARD_PACIFIC_TZ).valueOf();
// 后端只接受近 30 天：禁选美西今天之后、29 天前之前的日期，选得到的任意时刻都不早于 30 天前
const AS_OF_LOOKBACK_DAYS = 29;
const isAsOfDateDisabled = (d: Dayjs): boolean => {
    const today = toPacificWallClock(Date.now());
    return d.isAfter(today, 'day') || d.isBefore(today.subtract(AS_OF_LOOKBACK_DAYS, 'day'), 'day');
};

interface AsOfModalProps {
    open: boolean;
    asOfMs: number | null;
    getContainer: () => HTMLElement;
    onConfirm: (asOfMs: number) => void;
    onReset: () => void;
    onClose: () => void;
}

const AsOfModal: React.FC<AsOfModalProps> = ({
    open,
    asOfMs,
    getContainer,
    onConfirm,
    onReset,
    onClose,
}) => {
    const { t } = useTranslation();
    const [value, setValue] = useState<Dayjs | null>(null);
    useEffect(() => {
        if (open) setValue(toPacificWallClock(asOfMs ?? Date.now()));
    }, [open, asOfMs]);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={t('central.deliveryMonitor.clock.asOfTitle')}
            getContainer={getContainer}
            width={360}
            destroyOnClose
            footer={[
                <Button key="reset" onClick={onReset} disabled={asOfMs === null}>
                    {t('central.deliveryMonitor.clock.asOfReset')}
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    disabled={!value}
                    onClick={() => value && onConfirm(fromPacificWallClock(value))}
                >
                    {t('central.deliveryMonitor.clock.asOfConfirm')}
                </Button>,
            ]}
        >
            <DatePicker
                showTime
                allowClear={false}
                value={value}
                onChange={setValue}
                disabledDate={isAsOfDateDisabled}
                style={{ width: '100%' }}
                getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
            />
        </Modal>
    );
};

// —— HUB 单选下拉：clip-path 斜切按钮 + 悬浮菜单 —— //

interface HubDropdownProps {
    label: string;
    placeholder: string;
    options: LastMileSiteType[];
    value: number | undefined;
    onChange: (v: number | undefined) => void;
    loading: boolean;
}

const HubDropdown: React.FC<HubDropdownProps> = ({
    label,
    placeholder,
    options,
    value,
    onChange,
    loading,
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const currentLabel = useMemo(() => {
        if (value === undefined) return placeholder;
        return options.find((o) => Number(o.id) === value)
            ? hubLabel(options.find((o) => Number(o.id) === value)!)
            : placeholder;
    }, [value, options, placeholder]);

    return (
        <div ref={rootRef} style={{ position: 'relative' }}>
            <button
                type="button"
                className={styles.selectBtn}
                onClick={() => setOpen((o) => !o)}
            >
                <div>
                    <div className={styles.selectLbl}>{label}</div>
                    <div className={styles.selectVal}>{loading ? '...' : currentLabel}</div>
                </div>
                <span
                    className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ''}`}
                >
                    <DownOutlined />
                </span>
            </button>
            {open && (
                <div className={styles.selectMenu}>
                    {options.map((o) => {
                        const id = Number(o.id);
                        return (
                            <div
                                key={o.id}
                                className={`${styles.selectMenuItem} ${value === id ? styles.selectMenuItemOn : ''}`}
                                onClick={() => {
                                    onChange(id);
                                    setOpen(false);
                                }}
                            >
                                <span>{hubLabel(o)}</span>
                                <span>{o.warehouseCount ?? 0} WHS</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// —— WHS 单选下拉：与 HUB 同款斜切外壳 + 深色勾选菜单 —— //
// value 仍是数组（长度 0 或 1），沿用外层「WHS 必选 → 空数组时不发查询」的判断，避免联动改动扩散

interface WhsDropdownProps {
    label: string;
    placeholder: string;
    emptyLabel: string;
    summaryLabel: (count: number) => string;
    options: LastMileSiteType[];
    value: number[];
    onChange: (v: number[]) => void;
    loading: boolean;
    disabled: boolean;
}

const WhsDropdown: React.FC<WhsDropdownProps> = ({
    label,
    placeholder,
    emptyLabel,
    summaryLabel,
    options,
    value,
    onChange,
    loading,
    disabled,
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const currentLabel = useMemo(() => {
        if (value.length === 0) return placeholder;
        if (value.length === 1) {
            const o = options.find((x) => Number(x.id) === value[0]);
            return o ? siteLabel(o) : summaryLabel(1);
        }
        return summaryLabel(value.length);
    }, [value, options, placeholder, summaryLabel]);

    const pick = (id: number) => {
        // 单选：点即换成该项并收起菜单。不允许取消选（WHS 必选，取消会让页面无数据），
        // 想「换一个」直接点新项即可。
        if (!value.includes(id)) onChange([id]);
        setOpen(false);
    };

    return (
        <div ref={rootRef} style={{ position: 'relative' }}>
            <button
                type="button"
                className={styles.selectBtn}
                disabled={disabled}
                onClick={() => !disabled && setOpen((o) => !o)}
                style={{
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                }}
            >
                <div>
                    <div className={styles.selectLbl}>{label}</div>
                    <div className={styles.selectVal}>{loading ? '...' : currentLabel}</div>
                </div>
                <span className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ''}`}>
                    <DownOutlined />
                </span>
            </button>
            {open && !disabled && (
                <div className={styles.selectMenu}>
                    {options.length === 0 ? (
                        <div className={styles.selectMenuItem} style={{ color: '#5f9ac9', cursor: 'default' }}>
                            {emptyLabel}
                        </div>
                    ) : (
                        options.map((o) => {
                            const id = Number(o.id);
                            const on = value.includes(id);
                            return (
                                <div
                                    key={o.id}
                                    className={`${styles.selectMenuItem} ${on ? styles.selectMenuItemOn : ''}`}
                                    onClick={() => pick(id)}
                                >
                                    <span>{siteLabel(o)}</span>
                                    <span>{on ? '✓' : ''}</span>
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

// —— KPI 卡片 —— //

interface KpiCardProps {
    name: string;
    valueText: string;
    unit: string;
    deltaText: string | null;
    deltaValue: number | null;
    deltaSuffix: string;
    noComparisonLabel: string;
}

const KpiCard: React.FC<KpiCardProps> = ({
    name,
    valueText,
    unit,
    deltaText,
    deltaValue,
    deltaSuffix,
    noComparisonLabel,
}) => {
    const deltaClass =
        deltaValue === null || deltaValue === 0
            ? ''
            : deltaValue > 0
            ? styles.kpiDeltaUp
            : styles.kpiDeltaDown;
    const arrow = deltaValue === null || deltaValue === 0 ? '' : deltaValue > 0 ? '▲ ' : '▼ ';

    // 数字每次"可见变化"（valueText 变，即渲染出来的数值真的动了一格）时闪一次。
    // 因为 formatCount 是 Math.round 后 toLocaleString，tween 每 1s 步进但取整后不变的秒不会闪，
    // 观感上就是"数字跳一格 → 卡片一闪"。remove+reflow+add 是为了让同一个动画能立即重放。
    const cardRef = useRef<HTMLDivElement>(null);
    const numRef = useRef<HTMLElement>(null);
    useEffect(() => {
        const card = cardRef.current;
        const num = numRef.current;
        if (!card || !num) return;
        card.classList.remove(styles.kpiPulse);
        num.classList.remove(styles.kpiPulse);
        void card.offsetWidth;
        card.classList.add(styles.kpiPulse);
        num.classList.add(styles.kpiPulse);
    }, [valueText]);

    return (
        <div ref={cardRef} className={`${styles.panel} ${styles.kpi}`}>
            <div className={styles.kpiGlow} />
            <div className={styles.kpiSweep} />
            <div className={styles.kpiTop}>
                <span className={styles.kpiTag} aria-hidden>◆</span>
                <span className={styles.kpiName}>{name}</span>
            </div>
            <div className={styles.kpiV}>
                <b ref={numRef} className={styles.kpiVBig}>{valueText}</b>
                <em className={styles.kpiVUnit}>{unit}</em>
                <div className={styles.kpiFoot}>
                    <span>{deltaSuffix}</span>
                    {deltaText === null ? (
                        <span>{noComparisonLabel}</span>
                    ) : (
                        <span className={`${styles.kpiDelta} ${deltaClass}`}>
                            {arrow}
                            {deltaText.replace(/^[+-]/, '')}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

// —— 区域 3：司机列表 —— //

type BucketTab = 'ALL' | DeliveryMonitorProgressBucket;

type SortDir = 'DESC' | 'ASC';

interface DriverListPanelProps {
    drivers: DeliveryMonitorDriverType[];
    loading: boolean;
    bucketTab: BucketTab;
    onBucketTabChange: (tab: BucketTab) => void;
    sortBy: DeliveryMonitorDriverSort;
    onSortByChange: (v: DeliveryMonitorDriverSort) => void;
    sortDir: SortDir;
    onSortDirToggle: () => void;
    onSelectDriver: (driver: DeliveryMonitorDriverType) => void;
    selectedDriverId: string | null;
}

// 姓名首字母缩写。姓名在大屏上脱敏展示（法务要求），缩写只能取脱敏后仍可见的各词首字：
// 中文取首字，英文多词取首尾词首字母，单词取首字母。空则给 -
const initials = (name: string | null | undefined, fallback: string): string => {
    const s = (name || '').trim();
    if (!s) return fallback.slice(0, 2).toUpperCase();
    if (/[一-龥]/.test(s)) return s.slice(0, 1);
    const parts = s.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return s.slice(0, 1).toUpperCase();
};

const DriverListPanel: React.FC<DriverListPanelProps> = ({
    drivers,
    loading,
    bucketTab,
    onBucketTabChange,
    sortBy,
    onSortByChange,
    sortDir,
    onSortDirToggle,
    onSelectDriver,
    selectedDriverId,
}) => {
    const { t } = useTranslation();
    // drivers 是全量（query 不带 bucketFilter），计数才能数到每个桶；按 tab 过滤放在这里
    const counts = useMemo(() => {
        let leading = 0;
        let normal = 0;
        let lagging = 0;
        drivers.forEach((d) => {
            if (d.bucket === 'LEADING') leading++;
            else if (d.bucket === 'NORMAL') normal++;
            else lagging++;
        });
        return { all: drivers.length, leading, normal, lagging };
    }, [drivers]);

    const visibleDrivers = useMemo(
        () => (bucketTab === 'ALL' ? drivers : drivers.filter((d) => d.bucket === bucketTab)),
        [drivers, bucketTab],
    );

    // 滚动加载：先渲染一批，滚到底再追加。20s 一轮的 poll 不会重置，否则滚到第 5 批会被打回顶部
    const listRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(DRIVER_LIST_BATCH);
    useEffect(() => {
        setVisibleCount(DRIVER_LIST_BATCH);
        if (listRef.current) listRef.current.scrollTop = 0;
    }, [bucketTab, sortBy, sortDir]);

    const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        setVisibleCount((current) =>
            nextVisibleCount(current, visibleDrivers.length, {
                scrollTop: el.scrollTop,
                clientHeight: el.clientHeight,
                scrollHeight: el.scrollHeight,
            }),
        );
    };

    const rendered = visibleDrivers.slice(0, visibleCount);

    const tabs: Array<{ key: BucketTab; label: string; count: number; dotColor?: string }> = [
        { key: 'ALL', label: t('central.deliveryMonitor.driverList.tabAll'), count: counts.all },
        { key: 'LEADING', label: t('central.deliveryMonitor.driverList.tabLeading'), count: counts.leading, dotColor: BUCKET_COLORS.LEADING },
        { key: 'NORMAL', label: t('central.deliveryMonitor.driverList.tabNormal'), count: counts.normal, dotColor: BUCKET_COLORS.NORMAL },
        { key: 'LAGGING', label: t('central.deliveryMonitor.driverList.tabLagging'), count: counts.lagging, dotColor: BUCKET_COLORS.LAGGING },
    ];

    const sortOptions: Array<{ key: DeliveryMonitorDriverSort; label: string }> = [
        { key: 'DELIVERY_RATE', label: t('central.deliveryMonitor.driverList.sortDeliveryRate') },
        { key: 'ALLOCATED_PARCELS', label: t('central.deliveryMonitor.driverList.sortAllocated') },
        { key: 'DRIVER_NAME', label: t('central.deliveryMonitor.driverList.sortName') },
    ];

    return (
        <div
            className={styles.panel}
            style={{
                flex: '0 0 396px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            <div className={styles.phead}>
                <span className={styles.pheadDia} />
                <h2 className={styles.pheadTitle}>
                    {t('central.deliveryMonitor.regions.driverList')}
                </h2>
                <span className={styles.pheadHint}>
                    {t('central.deliveryMonitor.driverList.headerCount', {
                        count: visibleDrivers.length,
                    })}
                </span>
            </div>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(56,189,248,.14)', display: 'flex', flexDirection: 'column', gap: 9 }}>
                <div className={styles.chipRow}>
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={`${styles.chip} ${bucketTab === tab.key ? styles.chipOn : ''}`}
                            onClick={() => onBucketTabChange(tab.key)}
                        >
                            {tab.dotColor && <i style={{ background: tab.dotColor, color: tab.dotColor }} />}
                            {tab.label}
                            <span style={{ opacity: 0.72 }}>({tab.count})</span>
                        </button>
                    ))}
                </div>
                <div className={styles.sortBar}>
                    <span className={styles.sortLbl}>
                        {t('central.deliveryMonitor.driverList.sortLabel')}
                    </span>
                    <div className={styles.chipRow}>
                        {sortOptions.map((opt) => (
                            <button
                                key={opt.key}
                                type="button"
                                className={`${styles.chip} ${sortBy === opt.key ? styles.chipOn : ''}`}
                                onClick={() => onSortByChange(opt.key)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        className={styles.sortDirBtn}
                        onClick={onSortDirToggle}
                        title={String(
                            t(
                                sortDir === 'DESC'
                                    ? 'central.deliveryMonitor.driverList.sortDirDesc'
                                    : 'central.deliveryMonitor.driverList.sortDirAsc',
                            ),
                        )}
                    >
                        {sortDir === 'DESC' ? '▼' : '▲'}
                    </button>
                </div>
            </div>
            <div className={styles.dlist} ref={listRef} onScroll={handleListScroll}>
                {loading ? (
                    <div style={{ padding: 24, textAlign: 'center' }}>
                        <Spin />
                    </div>
                ) : visibleDrivers.length === 0 ? (
                    <div style={{ padding: 24, color: TEXT_DIM, textAlign: 'center', fontSize: 13 }}>
                        {t('central.deliveryMonitor.driverList.empty')}
                    </div>
                ) : (
                    rendered.map((d) => {
                        // 进度条宽度 & 百分数都从 deliveredCount/allocatedParcels 派生，
                        // 与大字展示的分子/分母保持同源，避免和后端 deliveryRate 不一致
                        const allocated = Number(d.allocatedParcels) || 0;
                        const delivered = Number(d.deliveredCount) || 0;
                        const ratePct = allocated > 0 ? (delivered / allocated) * 100 : 0;
                        const barWidth = Math.max(0, Math.min(100, ratePct));
                        const rateText = allocated > 0 ? `${ratePct.toFixed(2)}%` : '-';
                        const isOn = selectedDriverId === d.driverAccountId;
                        return (
                            <div
                                key={d.driverAccountId}
                                className={`${styles.row} ${isOn ? styles.rowOn : ''}`}
                                onClick={() => onSelectDriver(d)}
                            >
                                <div
                                    className={styles.av}
                                    style={{
                                        background: BUCKET_COLORS[d.bucket],
                                        // 原型六边形有淡淡的外发光，不是荧光棒 —— 3px + 半透明足够
                                        filter: `drop-shadow(0 0 3px ${BUCKET_COLORS[d.bucket]}80)`,
                                    }}
                                >
                                    {initials(d.driverName, d.driverAccountId)}
                                </div>
                                <div className="min-w-0">
                                    <div className={styles.r1}>
                                        <b title={maskPersonNameForExport(d.driverName) || d.driverAccountId}>
                                            {maskPersonNameForExport(d.driverName) || d.driverAccountId}
                                        </b>
                                    </div>
                                    <div className={styles.r2}>
                                        <div className={styles.bar}>
                                            <i style={{ width: `${barWidth}%` }} />
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.rstat}>
                                    <b style={{ color: BUCKET_COLORS[d.bucket] }}>
                                        {formatCount(d.deliveredCount)}/{formatCount(d.allocatedParcels)}
                                    </b>
                                    <em>{rateText}</em>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

// —— 司机详情弹窗（对齐 PRD showPop：贴在小车旁并随车移动，位置由 MapPanel 每帧写入） —— //

interface DriverPopupProps {
    rootRef: React.RefObject<HTMLDivElement>;
    driverAccountId: string;
    driverName: string | null;
    bucket: DeliveryMonitorProgressBucket;
    taskDate: string;
    /** 司机当前在地图上有小车时才能定位 */
    canLocate: boolean;
    replayLoading: boolean;
    onClose: () => void;
    onMessage: () => void;
    onLocate: () => void;
    onReplay: () => void;
}

const STOP_CLASSES: Record<DeliveryMonitorParcelStatus, string> = {
    DELIVERED: styles.stopDone,
    IN_DELIVERY: styles.stopCur,
    PENDING: '',
};

// 包裹时间按美西显示；缺失显示 --:--
const formatPacificHm = (ms: number | null | undefined): string =>
    ms === null || ms === undefined ? '--:--' : dayjs(Number(ms)).tz(DASHBOARD_PACIFIC_TZ).format('HH:mm');

const DriverPopup: React.FC<DriverPopupProps> = ({
    rootRef,
    driverAccountId,
    driverName,
    bucket,
    taskDate,
    canLocate,
    replayLoading,
    onClose,
    onMessage,
    onLocate,
    onReplay,
}) => {
    const { t } = useTranslation();
    const { data, loading } = useQuery<QueryDeliveryMonitorDriverDetailResponse>(
        QUERY_DELIVERY_MONITOR_DRIVER_DETAIL,
        {
            variables: { driverAccountId: Number(driverAccountId), taskDate },
            fetchPolicy: 'cache-and-network',
        },
    );
    const detail = data?.deliveryMonitorDriverDetail;
    const color = BUCKET_COLORS[bucket];
    const barWidth = Math.max(0, Math.min(100, Number(detail?.deliveryRate) || 0));

    const statusLabel = (status: DeliveryMonitorParcelStatus): string => {
        if (status === 'DELIVERED') return t('central.deliveryMonitor.detail.statusDelivered');
        if (status === 'IN_DELIVERY') return t('central.deliveryMonitor.detail.statusInDelivery');
        return t('central.deliveryMonitor.detail.statusPending');
    };

    const bucketLabel = (b: DeliveryMonitorProgressBucket): string => {
        if (b === 'LEADING') return t('central.deliveryMonitor.bucket.leading');
        if (b === 'LAGGING') return t('central.deliveryMonitor.bucket.lagging');
        return t('central.deliveryMonitor.bucket.normal');
    };

    return (
        <div ref={rootRef} className={styles.pop}>
            <div className={styles.popHead}>
                <div className={styles.popAv} style={{ background: color }}>
                    {initials(driverName, driverAccountId)}
                </div>
                <div>
                    <b>{maskPersonNameForExport(driverName) || driverAccountId}</b>
                </div>
                <span className={styles.popPill} style={{ color }}>
                    {bucketLabel(bucket)}
                </span>
                <button type="button" className={styles.popClose} onClick={onClose}>
                    ✕
                </button>
            </div>
            {detail && (
                <div className={styles.popStats}>
                    <div>
                        <b style={{ color: '#7ff0ff' }}>{formatCount(detail.allocatedParcels)}</b>
                        <span>{t('central.deliveryMonitor.detail.allocated')}</span>
                    </div>
                    <div>
                        <b style={{ color: '#34d399' }}>{formatCount(detail.deliveredCount)}</b>
                        <span>{t('central.deliveryMonitor.detail.delivered')}</span>
                    </div>
                    <div>
                        <b style={{ color: '#fbbf24' }}>{formatCount(detail.pendingCount)}</b>
                        <span>{t('central.deliveryMonitor.detail.pending')}</span>
                    </div>
                </div>
            )}
            <div className={styles.popBody}>
                {!detail ? (
                    loading ? (
                        <div style={{ padding: 24, textAlign: 'center' }}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.popEmpty}>{t('central.deliveryMonitor.detail.packageEmpty')}</div>
                    )
                ) : (
                    <>
                        <div className={styles.popLabel}>
                            <span>{t('central.deliveryMonitor.detail.progress')}</span>
                            <b style={{ color: '#7ff0ff' }}>{formatPercentFromString(detail.deliveryRate)}</b>
                        </div>
                        <div className={styles.popBar}>
                            <i style={{ width: `${barWidth}%` }} />
                        </div>
                        <div className={styles.popRtTitle}>
                            {t('central.deliveryMonitor.detail.packageTotal', { count: detail.packages.length })}
                        </div>
                        {detail.packages.length === 0 ? (
                            <div className={styles.popEmpty}>{t('central.deliveryMonitor.detail.packageEmpty')}</div>
                        ) : (
                            <div className={styles.stops}>
                                {detail.packages.map((pkg) => (
                                    <div
                                        key={pkg.trackingNumber}
                                        className={`${styles.stop} ${STOP_CLASSES[pkg.status]}`}
                                    >
                                        <span className={styles.stopDot} />
                                        <span className={styles.stopAdr} title={pkg.addressLine1 ?? undefined}>
                                            {pkg.addressLine1 ?? ''}
                                        </span>
                                        <span className={styles.stopTm}>
                                            {formatPacificHm(pkg.eventTimeMs)} · {statusLabel(pkg.status)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
                <div className={styles.popActions}>
                    <button type="button" onClick={onMessage}>
                        {t('central.deliveryMonitor.detail.actionMessage')}
                    </button>
                    <button type="button" disabled={!canLocate} onClick={onLocate}>
                        {t('central.deliveryMonitor.detail.actionLocate')}
                    </button>
                    <button type="button" disabled={replayLoading} onClick={onReplay}>
                        {t('central.deliveryMonitor.detail.actionReplay')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// —— 区域 4：地图 —— //

// 小车位置统一滞后 11 分钟（与后端 listDriverPositions 一致）；当日轨迹截止到同一时刻，回放终点与小车对齐
const POSITION_LAG_MS = 11 * 60_000;

// 从列表点开时，车不在视野（向内收缩 12%）内就 panTo（PRD pad(-.12)）
const PAN_INSET = 0.12;

interface SelectedDriver {
    id: string;
    name: string | null;
    bucket: DeliveryMonitorProgressBucket;
    fromList: boolean;
}

interface MapPanelProps {
    positions: DeliveryMonitorDriverPositionType[] | null | undefined;
    /** 当前所选 WHS，用于地图上标记仓位；仅带 lat/lng 的 WHS 会渲染 marker */
    whsMarkers: WhsMarker[];
    /** 当前所选 WHS 的 id；缺坐标的仓不在 whsMarkers 里，靠它判断是否换了仓 */
    warehouseId: string | null;
    isUnavailable: boolean;
    loading: boolean;
    onSelectDriver: (
        driverAccountId: string,
        driverName: string | null | undefined,
        bucket: DeliveryMonitorProgressBucket,
    ) => void;
    selectedDriver: SelectedDriver | null;
    onClosePopup: () => void;
    onToast: (msg: string) => void;
    taskDate: string;
    asOfMs: number | null;
    /** 切换 HUB / WHS / 指定时刻时变化，用于中止回放；切换后首轮没返回的车直接删，不变灰 */
    replayResetKey: string;
}

interface WhsMarker {
    id: string;
    label: string;
    latitude: number;
    longitude: number;
}

/**
 * 用 React 渲染 marker 内容到 Portal，避免每次位置轮询重建 DOM 导致的动画/事件重置。
 */
interface MarkerHost {
    marker: mapboxgl.Marker;
    element: HTMLDivElement;
    driverAccountId: string;
}

interface CarHost extends MarkerHost {
    /** 车身旋转层：朝向由 useCarGlide 直接写，车色/选中/皇冠由 portal 渲染进来 */
    rotor: HTMLDivElement;
    /** 闪烁层：包住光晕和 rotor，在动时由 useCarGlide 挂 carMoving */
    pulser: HTMLDivElement;
}

const MapPanel: React.FC<MapPanelProps> = ({
    positions,
    whsMarkers,
    warehouseId,
    isUnavailable,
    loading,
    onSelectDriver,
    selectedDriver,
    onClosePopup,
    onToast,
    taskDate,
    asOfMs,
    replayResetKey,
}) => {
    const { t } = useTranslation();
    const client = useApolloClient();
    const containerRef = useRef<HTMLDivElement>(null);
    // 地图区域：弹窗定位的参照系
    const areaRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const hostsRef = useRef<Map<string, CarHost>>(new Map());
    // 每个司机最近一次返回的数据：本轮没返回、变灰留着的车靠它渲染车色 / 名字 / 点击
    const lastSeenRef = useRef<Map<string, DeliveryMonitorDriverPositionType>>(new Map());
    // 上次处理 positions 时的范围；与当前不同 = 切换后的首轮
    const appliedScopeRef = useRef<string | null>(null);
    const scopeKeyRef = useRef(replayResetKey);
    scopeKeyRef.current = replayResetKey;
    const whsHostsRef = useRef<Map<string, MarkerHost>>(new Map());
    const fittedOnceRef = useRef(false);
    const [, forceRerender] = useState(0);
    const glide = useCarGlide(POLL_POSITIONS_MS, styles.carMoving);

    // 回放中的司机隐藏实时小车；记下来，轮询期间该车 marker 重建时也保持隐藏
    const hiddenCarIdRef = useRef<string | null>(null);
    const setLiveCarHidden = useCallback((driverAccountId: string, hidden: boolean) => {
        hiddenCarIdRef.current = hidden ? driverAccountId : null;
        const host = hostsRef.current.get(driverAccountId);
        if (host) host.element.style.visibility = hidden ? 'hidden' : '';
    }, []);
    const { replay, replayCarNode } = useRouteReplay(mapRef, setLiveCarHidden);
    const [replayLoading, setReplayLoading] = useState(false);

    const selectedRef = useRef(selectedDriver);
    selectedRef.current = selectedDriver;
    const closePopupRef = useRef(onClosePopup);
    closePopupRef.current = onClosePopup;

    useEffect(() => {
        if (isUnavailable) return;
        if (!containerRef.current || mapRef.current) return;
        try {
            const map = new mapboxgl.Map({
                container: containerRef.current,
                style: 'mapbox://styles/mapbox/dark-v11' as unknown as string,
                center: DEFAULT_CENTER,
                zoom: DEFAULT_ZOOM,
                attributionControl: false,
            });
            // 点地图空白处关闭弹窗；点小车时 mapbox 同样派发 click，按目标元素排除
            map.on('click', (e) => {
                const target = e.originalEvent.target as HTMLElement | null;
                if (target?.closest('.mapboxgl-marker')) return;
                if (selectedRef.current) closePopupRef.current();
            });
            mapRef.current = map;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[delivery-monitor] mapbox init failed', err);
        }
        const hosts = hostsRef.current;
        const whsHosts = whsHostsRef.current;
        const lastSeen = lastSeenRef.current;
        return () => {
            // 回放图层要在 map.remove 之前清掉
            replay.stop();
            glide.dispose();
            hosts.forEach((h) => h.marker.remove());
            hosts.clear();
            lastSeen.clear();
            whsHosts.forEach((h) => h.marker.remove());
            whsHosts.clear();
            mapRef.current?.remove();
            mapRef.current = null;
            fittedOnceRef.current = false;
        };
    }, [isUnavailable, replay, glide]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const hosts = hostsRef.current;
        const lastSeen = lastSeenRef.current;
        const nextIds = new Set<string>();

        (positions || []).forEach((p) => {
            nextIds.add(p.driverAccountId);
            lastSeen.set(p.driverAccountId, p);
            let host = hosts.get(p.driverAccountId);
            if (!host) {
                const element = document.createElement('div');
                element.style.cssText = 'width:34px;height:46px;';
                if (hiddenCarIdRef.current === p.driverAccountId) element.style.visibility = 'hidden';
                // element 的 transform 归 mapbox 定位、rotor 的归转向，闪烁动画单独占一层
                const pulser = document.createElement('div');
                pulser.style.cssText = 'width:34px;height:46px;position:relative;';
                const halo = document.createElement('div');
                halo.className = styles.carHalo;
                pulser.appendChild(halo);
                // 转向带 1.2s 过渡，与 PRD 一致；relative 让车身叠在光晕上面
                const rotor = document.createElement('div');
                rotor.style.cssText = 'width:34px;height:46px;position:relative;transition:transform 1.2s ease;';
                pulser.appendChild(rotor);
                element.appendChild(pulser);
                const marker = new mapboxgl.Marker({ element, anchor: 'center' })
                    .setLngLat([p.longitude, p.latitude])
                    .addTo(map);
                host = { marker, element, rotor, pulser, driverAccountId: p.driverAccountId };
                hosts.set(p.driverAccountId, host);
            }
            // 光晕用车本身颜色，车换桶时跟着变
            host.pulser.style.setProperty('--car-color', BUCKET_COLORS[p.bucket]);
            // 之前变灰的车本轮又返回了：恢复亮色，从当前位置接着滑
            host.pulser.classList.remove(styles.carLost);
            // 不直接跳到最新点：沿窗口内的轨迹点在一个轮询周期内滑过去
            glide.place(p.driverAccountId, host, p.track.map(toMotionPoint));
        });

        // 本轮没返回的车：平时变灰留在原地（滑到一半的先走完），切换范围后的首轮直接删
        const scopeChanged = appliedScopeRef.current !== scopeKeyRef.current;
        appliedScopeRef.current = scopeKeyRef.current;
        const { lost, removed } = planCarPresence(hosts.keys(), nextIds, scopeChanged);
        lost.forEach((id) => hosts.get(id)?.pulser.classList.add(styles.carLost));
        removed.forEach((id) => {
            hosts.get(id)?.marker.remove();
            hosts.delete(id);
            glide.remove(id);
            lastSeen.delete(id);
        });

        forceRerender((n) => n + 1);

        if (!fittedOnceRef.current && positions && positions.length > 0) {
            const bounds = new mapboxgl.LngLatBounds();
            positions.forEach((p) => bounds.extend([p.longitude, p.latitude]));
            if (!bounds.isEmpty()) {
                map.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 0 });
                fittedOnceRef.current = true;
            }
        }
    }, [positions, glide]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const whsHosts = whsHostsRef.current;
        const nextIds = new Set<string>();

        whsMarkers.forEach((w) => {
            nextIds.add(w.id);
            let host = whsHosts.get(w.id);
            if (!host) {
                const element = document.createElement('div');
                element.style.cssText = 'width:56px;height:66px;pointer-events:none;';
                // anchor: bottom 让房子底尖对准坐标点
                const marker = new mapboxgl.Marker({ element, anchor: 'bottom' })
                    .setLngLat([w.longitude, w.latitude])
                    .addTo(map);
                host = { marker, element, driverAccountId: w.id };
                whsHosts.set(w.id, host);
            } else {
                host.marker.setLngLat([w.longitude, w.latitude]);
            }
        });

        Array.from(whsHosts.keys()).forEach((id) => {
            if (!nextIds.has(id)) {
                whsHosts.get(id)?.marker.remove();
                whsHosts.delete(id);
            }
        });

        forceRerender((n) => n + 1);
    }, [whsMarkers]);

    // 换仓（含首次进入、切 HUB 后自动选仓）时直接跳到该仓；仓缺坐标则退回等小车位置到了按小车框一次
    const focusedWhsIdRef = useRef<string | null>(null);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const focus = whsFocusOnChange(focusedWhsIdRef.current, warehouseId, whsMarkers);
        focusedWhsIdRef.current = warehouseId;
        if (focus === null) return;
        if (focus.kind === 'center') {
            map.jumpTo({ center: [focus.lng, focus.lat], zoom: WHS_FOCUS_ZOOM });
            fittedOnceRef.current = true;
        } else {
            fittedOnceRef.current = false;
        }
    }, [warehouseId, whsMarkers]);

    // 弹窗贴车并随车移动：每帧按小车当前像素位置重算；司机无位置时停靠在地图中部
    const selectedId = selectedDriver?.id ?? null;
    useEffect(() => {
        if (selectedId === null) return;
        let raf = 0;
        let last = '';
        const loop = () => {
            const el = popupRef.current;
            const area = areaRef.current;
            if (el && area) {
                const map = mapRef.current;
                const host = hostsRef.current.get(selectedId);
                const anchor = map && host ? map.project(host.marker.getLngLat()) : null;
                const { x, y } = placePopup({
                    anchor: anchor ? { x: anchor.x, y: anchor.y } : null,
                    width: el.offsetWidth,
                    height: el.offsetHeight,
                    boundsWidth: area.clientWidth,
                    boundsHeight: area.clientHeight,
                });
                const key = `${x},${y}`;
                if (key !== last) {
                    el.style.left = `${x}px`;
                    el.style.top = `${y}px`;
                    el.style.visibility = 'visible';
                    last = key;
                }
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [selectedId]);

    // 从右侧列表点开：车不在视野内就 panTo
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !selectedDriver?.fromList) return;
        const host = hostsRef.current.get(selectedDriver.id);
        const b = map.getBounds();
        if (!host || !b) return;
        const { lng, lat } = host.marker.getLngLat();
        const view = { west: b.getWest(), east: b.getEast(), south: b.getSouth(), north: b.getNorth() };
        if (!containsWithInset(view, lng, lat, PAN_INSET)) {
            map.panTo([lng, lat], { duration: 700 });
        }
    }, [selectedDriver]);

    // 点其他车 / 列表行时中止回放（开始回放时会先关弹窗，选中变为 null，不会误停）
    useEffect(() => {
        if (selectedDriver) replay.stop();
    }, [selectedDriver, replay]);

    // 切换 HUB / WHS / 指定时刻时中止回放
    useEffect(() => {
        replay.stop();
    }, [replayResetKey, replay]);

    const handleLocate = useCallback(() => {
        const host = selectedDriver ? hostsRef.current.get(selectedDriver.id) : undefined;
        if (!host) return;
        mapRef.current?.flyTo({ center: host.marker.getLngLat(), zoom: 14, duration: 900 });
    }, [selectedDriver]);

    // 归位（PRD fitBtn）：先停回放，再框住全部小车当前位置 + 所选仓；padding / maxZoom 与首次自动框选一致
    const handleFitAll = useCallback(() => {
        replay.stop();
        const cars = Array.from(hostsRef.current.values(), (h) => h.marker.getLngLat());
        const b = fitAllBounds(cars, whsMarkers);
        if (!b) return;
        mapRef.current?.fitBounds(
            [
                [b.west, b.south],
                [b.east, b.north],
            ],
            { padding: 60, maxZoom: 12, duration: 800 },
        );
    }, [replay, whsMarkers]);

    const handleReplay = useCallback(async () => {
        if (!selectedDriver) return;
        const target = selectedDriver;
        replay.stop();
        setReplayLoading(true);
        try {
            const endMs = (asOfMs ?? Date.now()) - POSITION_LAG_MS;
            const { data } = await client.query<QueryDeliveryMonitorDriverTrajectoryResponse>({
                query: QUERY_DELIVERY_MONITOR_DRIVER_TRAJECTORY,
                variables: {
                    driverAccountId: target.id,
                    startTime: pacificDayStartIso(taskDate),
                    endTime: dayjs.utc(endMs).toISOString(),
                },
                fetchPolicy: 'network-only',
            });
            // null = CH 未配置；[] = 今日无点。两种都保持弹窗打开，只提示
            const points = data?.deliveryMonitorDriverTrajectory;
            if (points === null || points === undefined) {
                onToast(t('central.deliveryMonitor.detail.trajectoryUnavailable'));
                return;
            }
            if (points.length === 0) {
                onToast(t('central.deliveryMonitor.detail.trajectoryEmpty'));
                return;
            }
            // 等待期间用户关了弹窗或切到别的司机，就不再回放
            if (selectedRef.current?.id !== target.id) return;
            onClosePopup();
            replay.start(target.id, BUCKET_COLORS[target.bucket], points.map(toMotionPoint));
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[delivery-monitor] trajectory query failed', err);
            onToast(t('central.deliveryMonitor.detail.trajectoryUnavailable'));
        } finally {
            setReplayLoading(false);
        }
    }, [selectedDriver, replay, asOfMs, client, taskDate, onToast, onClosePopup, t]);

    // 不 memo：host 是 useEffect 里创建的，effect 跑完才 forceRerender；
    // 若 memo 依赖仅 whsMarkers，重渲染时引用未变会复用"host 尚未创建"时的空结果，portal 永远写不进去
    const whsMarkerNodes: React.ReactNode[] = [];
    whsMarkers.forEach((w) => {
        const host = whsHostsRef.current.get(w.id);
        if (!host) return;
        whsMarkerNodes.push(
            createPortal(
                <div
                    title={w.label}
                    style={{ position: 'relative', width: 56, height: 66, pointerEvents: 'none' }}
                >
                    <WhsMarkerIcon />
                    <i className={styles.whsRing} />
                </div>,
                host.element,
                w.id,
            ),
        );
    });

    // 同 whsMarkerNodes，不 memo：host 在 useEffect 里才创建。
    // 之前这里 memo 依赖 [positions, selectedId, kingId, onSelectDriver]，而 forceRerender 的计数没进依赖，
    // 于是「positions 变 → 本轮算出空 portal（host 还没建）→ effect 建 host → forceRerender → 依赖没变、复用空结果」，
    // 新司机的小车要等下一轮 positions 换引用才画出来。换仓时整批司机都是新的，表现就是地图空 20 秒。
    // 变灰的车不在本轮 positions 里，按 marker 逐个取数据：本轮返回的优先，否则用最近一次的
    const returnedById = new Map((positions || []).map((p) => [p.driverAccountId, p]));
    const cars: { host: CarHost; p: DeliveryMonitorDriverPositionType }[] = [];
    hostsRef.current.forEach((host, driverAccountId) => {
        const p = returnedById.get(driverAccountId) ?? lastSeenRef.current.get(driverAccountId);
        if (p) cars.push({ host, p });
    });
    // 皇冠：LEADING 桶里 (rate, allocated) 最大者，变灰的车也参与；数据在 ref 里，不能按 positions memo
    const kingId = pickKingId(cars.map((c) => c.p));
    const markerNodes: React.ReactNode[] = [];
    cars.forEach(({ host, p }) => {
        const selected = selectedId === p.driverAccountId;
        const isKing = kingId === p.driverAccountId;
        markerNodes.push(
            createPortal(
                <div
                    className={`${styles.carWrap} ${isKing ? styles.king : ''}`}
                    style={{
                        cursor: 'pointer',
                        // 非皇冠车按 bucket 色补一层轻发光；皇冠走 kingGlow 动画自带 filter，别在这里覆盖
                        filter: isKing
                            ? undefined
                            : `drop-shadow(0 0 4px ${BUCKET_COLORS[p.bucket]}80)`,
                    }}
                    title={maskPersonNameForExport(p.driverName) || p.driverAccountId}
                    onClick={() => onSelectDriver(p.driverAccountId, p.driverName, p.bucket)}
                >
                    {selected && <div className={styles.carSelectRing} />}
                    <CarIcon color={BUCKET_COLORS[p.bucket]} selected={selected} />
                    {isKing && (
                        <div className={styles.carCrown}>
                            <CrownIcon />
                        </div>
                    )}
                </div>,
                host.rotor,
                p.driverAccountId,
            ),
        );
    });

    return (
        <div
            className={styles.panel}
            style={{
                flex: 1,
                position: 'relative',
                overflow: 'hidden',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div className={styles.phead}>
                <span className={styles.pheadDia} />
                <h2 className={styles.pheadTitle}>{t('central.deliveryMonitor.regions.map')}</h2>
                <span className={styles.pheadHint}>{t('central.deliveryMonitor.map.hint')}</span>
            </div>
            <div ref={areaRef} style={{ position: 'relative', flex: 1, minHeight: 0 }}>
                {isUnavailable ? (
                    <div
                        style={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: TEXT_DIM,
                        }}
                    >
                        {t('central.deliveryMonitor.map.unavailable')}
                    </div>
                ) : (
                    <>
                        <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
                        {whsMarkerNodes}
                        {markerNodes}
                        {replayCarNode}
                        <div className={styles.mapStat}>
                            {loading
                                ? t('central.deliveryMonitor.map.loading')
                                : t('central.deliveryMonitor.map.pollHint')}
                        </div>
                        {!loading && (positions?.length ?? 0) === 0 && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    color: TEXT_DIM,
                                    fontSize: 13,
                                    pointerEvents: 'none',
                                }}
                            >
                                {t('central.deliveryMonitor.map.noPosition')}
                            </div>
                        )}
                        <div className={styles.mapLegend}>
                            <span>
                                <i style={{ background: BUCKET_COLORS.LEADING, color: BUCKET_COLORS.LEADING }} />
                                {t('central.deliveryMonitor.bucket.leading')}
                            </span>
                            <span>
                                <i style={{ background: BUCKET_COLORS.NORMAL, color: BUCKET_COLORS.NORMAL }} />
                                {t('central.deliveryMonitor.bucket.normal')}
                            </span>
                            <span>
                                <i style={{ background: BUCKET_COLORS.LAGGING, color: BUCKET_COLORS.LAGGING }} />
                                {t('central.deliveryMonitor.bucket.lagging')}
                            </span>
                            <span>
                                <i style={{ background: '#a78bfa', color: '#a78bfa' }} />
                                {t('central.deliveryMonitor.map.legendWhs')}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.fitBtn}
                            title={String(t('central.deliveryMonitor.map.fit'))}
                            onClick={handleFitAll}
                        >
                            {/* 不用 ⌖ 字符：字体里它的笔画偏下，按钮居中了图标仍不居中 */}
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                                <circle cx="8" cy="8" r="4.5" />
                                <path d="M8 1v14M1 8h14" />
                            </svg>
                        </button>
                        <div className={styles.zoomBar}>
                            <button
                                type="button"
                                className={styles.zoomBtn}
                                title={String(t('central.deliveryMonitor.map.zoomIn'))}
                                onClick={() => mapRef.current?.zoomIn()}
                            >
                                +
                            </button>
                            <button
                                type="button"
                                className={styles.zoomBtn}
                                title={String(t('central.deliveryMonitor.map.zoomOut'))}
                                onClick={() => mapRef.current?.zoomOut()}
                            >
                                −
                            </button>
                        </div>
                    </>
                )}
                {selectedDriver && (
                    <DriverPopup
                        key={selectedDriver.id}
                        rootRef={popupRef}
                        driverAccountId={selectedDriver.id}
                        driverName={selectedDriver.name}
                        bucket={selectedDriver.bucket}
                        taskDate={taskDate}
                        canLocate={hostsRef.current.has(selectedDriver.id)}
                        replayLoading={replayLoading}
                        onClose={onClosePopup}
                        onMessage={() => onToast(t('central.deliveryMonitor.detail.comingSoon'))}
                        onLocate={handleLocate}
                        onReplay={handleReplay}
                    />
                )}
            </div>
        </div>
    );
};

// —— 页面 —— //

// 大屏对齐 1920×1080 底稿：外层按视口宽高各自独立 scale（非等比），铺满不留黑边。
// 参照原型 HTML 里 `fit()` 的做法：原型用 min() 会在非 16:9 视口两端留黑边；
// 这里侧边栏 + 语言切换等 chrome 让 available 区域少见严格 16:9，用户不希望看到黑边，
// 因此 scaleX / scaleY 独立计算，接受轻微拉伸（地图圆点会略变椭圆，但整体观感优于黑边）。
const STAGE_W = 1920;
const STAGE_H = 1080;

const DeliveryMonitorPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const rootRef = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [stageScale, setStageScale] = useState<{ x: number; y: number }>({ x: 1, y: 1 });

    useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const compute = () => {
            const sx = el.clientWidth / STAGE_W;
            const sy = el.clientHeight / STAGE_H;
            setStageScale({
                x: sx > 0 ? sx : 1,
                y: sy > 0 ? sy : 1,
            });
        };
        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const [hubId, setHubId] = useState<number | undefined>(undefined);
    const [warehouseIds, setWarehouseIds] = useState<number[]>([]);
    const [bucketTab, setBucketTab] = useState<BucketTab>('ALL');
    const [sortBy, setSortBy] = useState<DeliveryMonitorDriverSort>('DELIVERY_RATE');
    // 后端只按 DESC 返回，前端翻转即可；默认 DESC，点击 ▼ 切 ASC
    const [sortDir, setSortDir] = useState<SortDir>('DESC');
    const [selectedDriver, setSelectedDriver] = useState<SelectedDriver | null>(null);
    const now = useNow();
    // 指定查询时刻：从 baseMs 起跟真实时间一起走（setAtMs = 设定时的真实时间）；null = 实时
    const [asOfAnchor, setAsOfAnchor] = useState<{ baseMs: number; setAtMs: number } | null>(null);
    const [asOfOpen, setAsOfOpen] = useState(false);
    const asOfElapsedMs = asOfAnchor !== null ? Math.max(0, now.getTime() - asOfAnchor.setAtMs) : 0;
    const clockDate = useMemo(
        () => (asOfAnchor !== null ? new Date(asOfAnchor.baseMs + asOfElapsedMs) : now),
        [asOfAnchor, asOfElapsedMs, now],
    );
    // 查询用的指定时刻按位置轮询周期步进：每 20s 变一次，变量变化即触发位置刷新，小车随之移动
    const asOfMs =
        asOfAnchor !== null
            ? asOfAnchor.baseMs + Math.floor(asOfElapsedMs / POLL_POSITIONS_MS) * POLL_POSITIONS_MS
            : null;
    // KPI / 司机列表的指定时刻取整到分钟：数仓 slot 粒度为分钟，每分钟才变一次变量，后端缓存也按分钟命中
    const asOfMinuteMs = asOfMs !== null ? asOfMs - (asOfMs % MINUTE_MS) : null;

    useEffect(() => {
        const onChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const toggleFullscreen = useCallback(() => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            rootRef.current?.requestFullscreen();
        }
    }, []);

    const isZh = (i18n.resolvedLanguage || i18n.language || 'en').startsWith('zh');
    const toggleLanguage = useCallback(() => {
        i18n.changeLanguage(isZh ? 'en' : 'zh');
    }, [i18n, isZh]);

    // 大屏统一按美西时间显示（不跟随浏览器时区）
    const ptTimeText = useMemo(
        () =>
            new Intl.DateTimeFormat('en-GB', {
                timeZone: 'America/Los_Angeles',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hourCycle: 'h23',
            }).format(clockDate),
        [clockDate],
    );
    const ptDateText = useMemo(
        () =>
            new Intl.DateTimeFormat(isZh ? 'zh-CN' : 'en-US', {
                timeZone: 'America/Los_Angeles',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                weekday: 'short',
            }).format(clockDate),
        [clockDate, isZh],
    );

    const { data: hubsData, loading: hubsLoading } = useQuery<QueryDeliveryMonitorHubsResponse>(
        QUERY_DELIVERY_MONITOR_HUBS,
        { fetchPolicy: 'cache-and-network' },
    );
    const hubs = useMemo(() => {
        const list = hubsData?.deliveryMonitorHubs ?? [];
        return [...list].sort((a, b) =>
            hubLabel(a).localeCompare(hubLabel(b), undefined, { sensitivity: 'base' }),
        );
    }, [hubsData?.deliveryMonitorHubs]);

    const { data: warehousesData, loading: warehousesLoading } =
        useQuery<QueryDeliveryMonitorWarehousesResponse>(QUERY_DELIVERY_MONITOR_WAREHOUSES, {
            variables: { hubId },
            skip: hubId === undefined,
            fetchPolicy: 'cache-and-network',
        });
    const warehouses = useMemo(() => {
        const list = warehousesData?.deliveryMonitorWarehouses ?? [];
        return [...list].sort((a, b) =>
            siteLabel(a).localeCompare(siteLabel(b), undefined, { sensitivity: 'base' }),
        );
    }, [warehousesData?.deliveryMonitorWarehouses]);

    // 与左上角 WMS 仓库树双向联动：首次进入按树选，之后大屏切仓推给树、树切 WHS 拉回大屏
    const client = useApolloClient();
    const { currentWarehouse: treeSite, changeWarehouse } = useWarehouseContext();
    const { hubId: treeHubId, whsId: treeWhsId } = treeSitePick(treeSite);
    // 以下 ref 只在 effect 里读最新值，不作为触发条件
    const treePickRef = useRef({ hubId: treeHubId, whsId: treeWhsId });
    treePickRef.current = { hubId: treeHubId, whsId: treeWhsId };
    const changeWarehouseRef = useRef(changeWarehouse);
    changeWarehouseRef.current = changeWarehouse;
    const hubsRef = useRef(hubs);
    hubsRef.current = hubs;
    const hubIdRef = useRef(hubId);
    hubIdRef.current = hubId;
    const warehouseIdsRef = useRef(warehouseIds);
    warehouseIdsRef.current = warehouseIds;

    // 首次进入跟随树：树选 WHS 取它的上级 HUB，树选 HUB 取它本身；不在可选列表里就默认第一个
    useEffect(() => {
        if (hubId === undefined && hubs.length > 0) {
            setHubId(pickSavedSiteId(hubs, treePickRef.current.hubId));
        }
    }, [hubs, hubId]);

    // 每次 hub 切换后 WHS 被清空，等该 HUB 的仓列表返回再自动补（WHS 单选且不可清空，空只会来自切 HUB / 首次进入）
    useEffect(() => {
        if (hubId === undefined) return;
        if (warehouseIds.length > 0) return;
        if (warehouses.length === 0) return;
        // 优先选树上选中的 WHS；树选的是 HUB、或换了 HUB 后该仓不在新列表里，回退第一个
        const whsId = pickSavedSiteId(warehouses, treePickRef.current.whsId);
        if (whsId !== undefined) setWarehouseIds([whsId]);
    }, [hubId, warehouses, warehouseIds]);

    // 大屏的 WHS 定下来（首次自动补 / 切 HUB 后自动补 / 手动切 WHS）后让树选中它；树里没有这个仓时树不动
    const selectedWhsId = warehouseIds[0];
    useEffect(() => {
        if (selectedWhsId === undefined) return;
        changeWarehouseRef.current?.(String(selectedWhsId), SiteType.WAREHOUSE);
    }, [selectedWhsId]);

    // 树切到另一个 WHS → 大屏连同上级 HUB 一起切；该仓不在大屏列表里（测试仓、非末端 HUB、无权限）或查询失败时大屏不动。
    // 树点 HUB 节点会按 WMS 现有规则跳到 HUB 菜单页、离开大屏，这里不处理
    useEffect(() => {
        if (treeWhsId === null || treeHubId === null) return;
        // 首次进入由上面两个 effect 按树选好，这里只管之后的切换
        if (hubIdRef.current === undefined) return;
        if (String(warehouseIdsRef.current[0]) === treeWhsId) return;
        if (!hubsRef.current.some((h) => h.id === treeHubId)) return;
        let cancelled = false;
        client
            .query<QueryDeliveryMonitorWarehousesResponse>({
                query: QUERY_DELIVERY_MONITOR_WAREHOUSES,
                variables: { hubId: Number(treeHubId) },
            })
            .then(({ data }) => {
                if (cancelled) return;
                const list = data?.deliveryMonitorWarehouses ?? [];
                if (!list.some((w) => w.id === treeWhsId)) return;
                setHubId(Number(treeHubId));
                setWarehouseIds([Number(treeWhsId)]);
                setSelectedDriver(null);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [treeWhsId, treeHubId, client]);

    // 任务日取美西日期：实时跟 now、指定查询时刻跟 asOfMs 走，跨美西零点自动切日
    const taskDate = pacificTaskDate(asOfMs ?? now.getTime());

    const filterVariables = useMemo(
        () => ({
            taskDate,
            hubId: hubId ?? null,
            warehouseIds: warehouseIds.length > 0 ? warehouseIds : null,
        }),
        [taskDate, hubId, warehouseIds],
    );

    // WHS 必选：无 WHS 就不打 KPI/司机/位置接口
    const canQuery = warehouseIds.length > 0;

    // 指定查询时刻时按锚点单独存基线：实时写入的大值不能压住回看的历史值；同一锚点随时间前进仍只增不减
    const asOfBaseMs = asOfAnchor?.baseMs ?? null;
    const kpiStorageKey = useMemo(() => {
        const whs =
            warehouseIds.length > 0
                ? [...warehouseIds].sort((a, b) => a - b).join(',')
                : 'all';
        const asOf = asOfBaseMs !== null ? `:asof:${asOfBaseMs}` : '';
        return `deliveryMonitor:kpi:${taskDate}:${hubId ?? 'all'}:${whs}${asOf}`;
    }, [taskDate, hubId, warehouseIds, asOfBaseMs]);

    const { data: kpiDataRaw, loading: kpiLoading } =
        useQuery<QueryDeliveryMonitorDailyKpiWithPreviousResponse>(
            QUERY_DELIVERY_MONITOR_DAILY_KPI_WITH_PREVIOUS,
            {
                variables: { ...filterVariables, asOfMs: asOfMinuteMs },
                skip: !canQuery,
                fetchPolicy: 'cache-and-network',
                // 指定时刻由 asOfMinuteMs 步进驱动刷新，不再轮询
                pollInterval: asOfMinuteMs === null ? POLL_KPI_MS : 0,
            },
        );
    const kpiData = useScopedPrevious(kpiDataRaw, kpiStorageKey);
    const kpiHistory = kpiData?.deliveryMonitorDailyKpiWithPrevious;
    const currentKpi: DeliveryMonitorKpiSnapshotType | null = kpiHistory?.current ?? null;
    const previousKpi: DeliveryMonitorKpiSnapshotType | null = kpiHistory?.previous ?? null;
    const yesterdayKpi: DeliveryMonitorKpiSnapshotType | null = kpiHistory?.yesterday ?? null;

    useEffect(() => {
        try {
            const prefix = 'deliveryMonitor:kpi:';
            const currentPrefix = `${prefix}${taskDate}:`;
            for (let i = sessionStorage.length - 1; i >= 0; i--) {
                const k = sessionStorage.key(i);
                if (k && k.startsWith(prefix) && !k.startsWith(currentPrefix)) {
                    sessionStorage.removeItem(k);
                }
            }
        } catch {
            // ignore
        }
    }, [taskDate]);
    // 补间总时长对齐 KPI poll 周期：new slot 一到就正好走完，观感上是"10 分钟慢慢加"
    const animated = useKpiInterpolation(currentKpi, previousKpi, kpiStorageKey, POLL_KPI_MS);

    const { data: driversDataRaw, loading: driversLoading } =
        useQuery<QueryDeliveryMonitorDriversResponse>(QUERY_DELIVERY_MONITOR_DRIVERS, {
            variables: {
                ...filterVariables,
                // 恒取全量：tab 计数要数全部桶，按 tab 传 bucketFilter 会让另外三个 tab 恒显示 0；
                // 且切 tab 不再重新请求（后端 cache key 也不含 bucket），切换是瞬时的
                bucketFilter: null,
                sortBy,
                asOfMs: asOfMinuteMs,
            },
            skip: !canQuery,
            fetchPolicy: 'cache-and-network',
            pollInterval: asOfMinuteMs === null ? POLL_DRIVERS_MS : 0,
        });
    const driversData = useScopedPrevious(driversDataRaw, `${kpiStorageKey}|${sortBy}`);
    const drivers = useMemo(() => {
        const list = driversData?.deliveryMonitorDrivers ?? [];
        return sortDir === 'DESC' ? list : [...list].reverse();
    }, [driversData?.deliveryMonitorDrivers, sortDir]);

    const {
        data: positionsData,
        loading: positionsLoading,
        previousData: positionsPrevious,
    } = useQuery<QueryDeliveryMonitorDriverPositionsResponse>(QUERY_DELIVERY_MONITOR_DRIVER_POSITIONS, {
        variables: { ...filterVariables, asOfMs },
        skip: !canQuery,
        fetchPolicy: 'network-only',
        // 指定时刻由 asOfMs 步进驱动刷新，不再轮询，避免同一轮打两次
        pollInterval: asOfMs === null ? POLL_POSITIONS_MS : 0,
    });
    const positions =
        positionsData?.deliveryMonitorDriverPositions ??
        positionsPrevious?.deliveryMonitorDriverPositions ??
        undefined;
    const isMapUnavailable = positions === null;

    // 地图上仅画当前选中的 WHS；lat/lng 缺失（DB 允许 null）直接过滤，不弹错
    const whsMarkers: WhsMarker[] = useMemo(() => {
        if (warehouseIds.length === 0) return [];
        const selected = new Set(warehouseIds);
        return warehouses
            .filter((w) => selected.has(Number(w.id)))
            .flatMap((w) => {
                if (!w.latitude || !w.longitude) return [];
                const lat = Number(w.latitude);
                const lng = Number(w.longitude);
                if (Number.isNaN(lat) || Number.isNaN(lng)) return [];
                return [{ id: String(w.id), label: siteLabel(w), latitude: lat, longitude: lng }];
            });
    }, [warehouses, warehouseIds]);

    const handleSelectDriverFromMap = useCallback(
        (id: string, name: string | null | undefined, bucket: DeliveryMonitorProgressBucket) => {
            setSelectedDriver({ id, name: name ?? null, bucket, fromList: false });
        },
        [],
    );
    const handleSelectDriverFromList = useCallback((d: DeliveryMonitorDriverType) => {
        setSelectedDriver({ id: d.driverAccountId, name: d.driverName ?? null, bucket: d.bucket, fromList: true });
    }, []);
    const handleClosePopup = useCallback(() => setSelectedDriver(null), []);

    // 大屏内的轻提示（对齐 PRD toast：2.4s 后淡出）
    const [toast, setToast] = useState<{ msg: string; show: boolean }>({ msg: '', show: false });
    const toastTimerRef = useRef<number | undefined>(undefined);
    const showToast = useCallback((msg: string) => {
        setToast({ msg, show: true });
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = window.setTimeout(() => setToast((s) => ({ ...s, show: false })), 2400);
    }, []);
    useEffect(() => () => window.clearTimeout(toastTimerRef.current), []);

    const displayScheduled = animated?.scheduledParcels ?? currentKpi?.scheduledParcels ?? null;
    const displayPicked = animated?.actualPickedUp ?? currentKpi?.actualPickedUp ?? null;
    const displayDelivered = animated?.deliveredParcels ?? currentKpi?.deliveredParcels ?? null;
    const displayPickupRate =
        animated?.pickupRate ?? (currentKpi?.pickupRate ? Number(currentKpi.pickupRate) : null);
    const displayDeliveryRate =
        animated?.deliveryRate ?? (currentKpi?.deliveryRate ? Number(currentKpi.deliveryRate) : null);

    const scheduledYoY = computeCountDelta(currentKpi?.scheduledParcels, yesterdayKpi?.scheduledParcels);
    const pickedYoY = computeCountDelta(currentKpi?.actualPickedUp, yesterdayKpi?.actualPickedUp);
    const deliveredYoY = computeCountDelta(currentKpi?.deliveredParcels, yesterdayKpi?.deliveredParcels);
    const pickupRateYoY = computeRateDeltaPp(currentKpi?.pickupRate, yesterdayKpi?.pickupRate);
    const deliveryRateYoY = computeRateDeltaPp(currentKpi?.deliveryRate, yesterdayKpi?.deliveryRate);

    const parcelUnit = t('central.deliveryMonitor.kpi.unitParcel');
    const percentUnit = '%';
    const kpiCards = currentKpi
        ? [
              {
                  name: t('central.deliveryMonitor.kpi.scheduledParcels'),
                  valueText: formatCount(displayScheduled),
                  unit: parcelUnit,
                  deltaText: formatCountDeltaPercent(scheduledYoY),
                  deltaValue: scheduledYoY,
              },
              {
                  name: t('central.deliveryMonitor.kpi.actualPickedUp'),
                  valueText: formatCount(displayPicked),
                  unit: parcelUnit,
                  deltaText: formatCountDeltaPercent(pickedYoY),
                  deltaValue: pickedYoY,
              },
              {
                  name: t('central.deliveryMonitor.kpi.deliveredParcels'),
                  valueText: formatCount(displayDelivered),
                  unit: parcelUnit,
                  deltaText: formatCountDeltaPercent(deliveredYoY),
                  deltaValue: deliveredYoY,
              },
              {
                  name: t('central.deliveryMonitor.kpi.pickupRate'),
                  valueText: formatPercentNumber(displayPickupRate),
                  unit: percentUnit,
                  deltaText: formatRateDeltaPp(pickupRateYoY),
                  deltaValue: pickupRateYoY,
              },
              {
                  name: t('central.deliveryMonitor.kpi.deliveryRate'),
                  valueText: formatPercentNumber(displayDeliveryRate),
                  unit: percentUnit,
                  deltaText: formatRateDeltaPp(deliveryRateYoY),
                  deltaValue: deliveryRateYoY,
              },
          ]
        : [];

    return (
        <div
            ref={rootRef}
            className="flex-1 min-h-0 -m-6"
            style={{
                position: 'relative',
                overflow: 'hidden',
                background: '#020a14',
            }}
        >
        <div
            className={styles.stage}
            style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: STAGE_W,
                height: STAGE_H,
                transform: `translate(-50%, -50%) scale(${stageScale.x}, ${stageScale.y})`,
                transformOrigin: 'center center',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            {/* 顶部 */}
            <div className={styles.header}>
                <div className={styles.brand}>
                    <img src={swiftxLogo} alt="SwiftX" className={styles.brandLogo} />
                    <div>
                        <div className={styles.brandTitle}>
                            {t('central.deliveryMonitor.pageTitle')}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <HubDropdown
                        label={t('central.deliveryMonitor.filterHub')}
                        placeholder={t('central.deliveryMonitor.filterAll')}
                        options={hubs}
                        value={hubId}
                        onChange={(v) => {
                            setHubId(v);
                            setWarehouseIds([]);
                            setSelectedDriver(null);
                        }}
                        loading={hubsLoading}
                    />
                    <WhsDropdown
                        label={t('central.deliveryMonitor.filterWarehouse')}
                        placeholder={t('central.deliveryMonitor.filterAll')}
                        emptyLabel={t('central.deliveryMonitor.filter.warehouseEmpty')}
                        summaryLabel={(count) =>
                            t('central.deliveryMonitor.filter.warehouseSelected', { count })
                        }
                        options={warehouses}
                        value={warehouseIds}
                        onChange={(v) => {
                            setWarehouseIds(v);
                            setSelectedDriver(null);
                        }}
                        loading={warehousesLoading}
                        disabled={hubId === undefined}
                    />
                </div>

                <div style={{ flex: 1 }} />

                {asOfMs === null && (
                    <div className={styles.live}>
                        <i className={styles.liveDot} />
                        LIVE
                    </div>
                )}

                <div
                    className={styles.clock}
                    onClick={(e) => {
                        // 隐藏入口：三连击打开「指定查询时刻」
                        if (e.detail !== 3) return;
                        window.getSelection()?.removeAllRanges();
                        setAsOfOpen(true);
                    }}
                >
                    <div className={styles.clockTime}>
                        {ptTimeText}
                        <span className={styles.clockTz}>{t('central.deliveryMonitor.clock.tzLabel')}</span>
                    </div>
                    <div className={styles.clockDate}>{ptDateText}</div>
                </div>

                <button
                    type="button"
                    className={styles.hbtn}
                    onClick={toggleLanguage}
                    title={String(t('central.deliveryMonitor.actions.switchLanguage'))}
                >
                    {isZh ? 'EN' : '中'}
                </button>

                <button
                    type="button"
                    className={styles.hbtn}
                    onClick={toggleFullscreen}
                    title={String(
                        t(
                            isFullscreen
                                ? 'central.deliveryMonitor.actions.exitFullscreen'
                                : 'central.deliveryMonitor.actions.fullscreen',
                        ),
                    )}
                >
                    {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                </button>
            </div>

            {/* KPI */}
            <div
                style={{
                    position: 'relative',
                    zIndex: 4,
                    flex: '0 0 126px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: 14,
                    padding: '12px 24px 8px',
                }}
            >
                {kpiLoading && !currentKpi
                    ? Array.from({ length: 5 }).map((_, i) => (
                          <div
                              key={i}
                              className={`${styles.panel} ${styles.kpi}`}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                              <Spin />
                          </div>
                      ))
                    : kpiCards.map((card) => (
                          <KpiCard
                              key={card.name}
                              name={card.name}
                              valueText={card.valueText}
                              unit={card.unit}
                              deltaText={card.deltaText}
                              deltaValue={card.deltaValue}
                              deltaSuffix={t('central.deliveryMonitor.kpi.deltaSuffix')}
                              noComparisonLabel={t('central.deliveryMonitor.kpi.noComparison')}
                          />
                      ))}
            </div>

            {/* Main：地图在左，司机列表在右 */}
            <div
                style={{
                    position: 'relative',
                    zIndex: 4,
                    flex: 1,
                    display: 'flex',
                    gap: 16,
                    padding: '6px 24px 20px',
                    minHeight: 0,
                }}
            >
                <MapPanel
                    positions={positions ?? []}
                    whsMarkers={whsMarkers}
                    warehouseId={warehouseIds.length > 0 ? String(warehouseIds[0]) : null}
                    isUnavailable={isMapUnavailable}
                    loading={positionsLoading}
                    onSelectDriver={handleSelectDriverFromMap}
                    selectedDriver={selectedDriver}
                    onClosePopup={handleClosePopup}
                    onToast={showToast}
                    taskDate={taskDate}
                    asOfMs={asOfMs}
                    replayResetKey={`${hubId ?? ''}|${warehouseIds.join(',')}|${asOfAnchor?.setAtMs ?? ''}`}
                />
                <DriverListPanel
                    drivers={drivers}
                    loading={driversLoading && drivers.length === 0}
                    bucketTab={bucketTab}
                    onBucketTabChange={setBucketTab}
                    sortBy={sortBy}
                    onSortByChange={(v) => {
                        if (v === sortBy) return;
                        setSortBy(v);
                        // 同 PRD：切到姓名默认 A→Z，切到妥投率 / 领取量默认从高到低
                        setSortDir(v === 'DRIVER_NAME' ? 'ASC' : 'DESC');
                    }}
                    sortDir={sortDir}
                    onSortDirToggle={() => setSortDir((d) => (d === 'DESC' ? 'ASC' : 'DESC'))}
                    onSelectDriver={handleSelectDriverFromList}
                    selectedDriverId={selectedDriver?.id ?? null}
                />
            </div>

            <div className={`${styles.toast} ${toast.show ? styles.toastShow : ''}`}>
                <i />
                <span>{toast.msg}</span>
            </div>

            <AsOfModal
                open={asOfOpen}
                asOfMs={asOfAnchor?.baseMs ?? null}
                getContainer={() => rootRef.current ?? document.body}
                onConfirm={(ms) => {
                    setAsOfAnchor({ baseMs: ms, setAtMs: Date.now() });
                    setAsOfOpen(false);
                }}
                onReset={() => {
                    setAsOfAnchor(null);
                    setAsOfOpen(false);
                }}
                onClose={() => setAsOfOpen(false)}
            />
        </div>
        </div>
    );
};

export default DeliveryMonitorPage;
